import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const MIN_COLUMN_WIDTH = 60;
const FALLBACK_COLUMN_WIDTH = 150;

const readStoredWidths = (storageKey) => {
  if (!storageKey) return {};
  try {
    const stored = localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    console.error("Error loading column widths from localStorage:", e);
    return {};
  }
};

const writeStoredWidths = (storageKey, widths) => {
  if (!storageKey) return;
  try {
    if (Object.keys(widths).length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    }
  } catch (e) {
    console.error("Error saving column widths to localStorage:", e);
  }
};

// Width a column falls back to when it is reset while the table is in fixed layout.
const defaultWidthFor = (column, containerWidth) => {
  const { width } = column;
  if (typeof width === "number") return width;
  if (typeof width === "string") {
    const value = parseFloat(width);
    if (!Number.isNaN(value)) {
      if (width.trim().endsWith("%")) return (value / 100) * containerWidth;
      return value;
    }
  }
  return FALLBACK_COLUMN_WIDTH;
};

/**
 * Drag-to-resize column widths for the DataTable.
 * Until a column is resized the table keeps its normal auto layout. On the first drag every
 * header's rendered width is measured and frozen, so the table can switch to fixed layout
 * with explicit pixel widths. Widths persist in localStorage when a storageKey is given.
 */
export default function useColumnResize({ storageKey, columns, tableRef }) {
  const [widths, setWidths] = useState(() => readStoredWidths(storageKey));
  const widthsRef = useRef(widths);
  widthsRef.current = widths;
  const cleanupRef = useRef(null);

  // Reload when the table switches to another object type
  useEffect(() => {
    setWidths(readStoredWidths(storageKey));
  }, [storageKey]);

  // Remove listeners if unmounted mid-drag
  useEffect(() => () => cleanupRef.current && cleanupRef.current(), []);

  // Only widths for columns that currently exist; stale keys are ignored
  const columnWidths = useMemo(() => {
    const active = {};
    columns.forEach((column) => {
      const width = widths[column.id];
      if (typeof width === "number" && width > 0) active[column.id] = Math.max(MIN_COLUMN_WIDTH, width);
    });
    return Object.keys(active).length > 0 ? active : null;
  }, [columns, widths]);

  const measureWidths = useCallback(() => {
    const measured = {};
    const table = tableRef.current;
    if (!table) return measured;
    table.querySelectorAll("th[data-col-key]").forEach((th) => {
      measured[th.getAttribute("data-col-key")] = Math.round(th.getBoundingClientRect().width);
    });
    return measured;
  }, [tableRef]);

  const startResize = useCallback(
    (columnId, event) => {
      event.preventDefault();
      event.stopPropagation();

      const base = { ...measureWidths(), ...(columnWidths || {}) };
      const startX = event.clientX;
      const startWidth = base[columnId] || FALLBACK_COLUMN_WIDTH;
      setWidths(base);

      const { body } = document;
      const previousCursor = body.style.cursor;
      const previousUserSelect = body.style.userSelect;
      body.style.cursor = "col-resize";
      body.style.userSelect = "none";

      const onMove = (moveEvent) => {
        const nextWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + moveEvent.clientX - startX));
        setWidths((prev) => (prev[columnId] === nextWidth ? prev : { ...prev, [columnId]: nextWidth }));
      };
      const cleanup = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        body.style.cursor = previousCursor;
        body.style.userSelect = previousUserSelect;
        cleanupRef.current = null;
      };
      function onUp() {
        cleanup();
        writeStoredWidths(storageKey, widthsRef.current);
      }

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      cleanupRef.current = cleanup;
    },
    [columnWidths, measureWidths, storageKey]
  );

  const resetColumn = useCallback(
    (columnId, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const column = columns.find((col) => col.id === columnId);
      if (!column || !columnWidths || columnWidths[columnId] === undefined) return;

      const next = { ...columnWidths };
      const others = Object.keys(next).filter((key) => key !== columnId);
      if (others.length === 0) {
        // Nothing else is sized, so fall back to the original auto layout
        delete next[columnId];
      } else {
        const containerWidth = tableRef.current?.parentElement?.clientWidth || 0;
        next[columnId] = Math.max(MIN_COLUMN_WIDTH, Math.round(defaultWidthFor(column, containerWidth)));
      }
      setWidths(next);
      writeStoredWidths(storageKey, next);
    },
    [columns, columnWidths, storageKey, tableRef]
  );

  return { columnWidths, startResize, resetColumn };
}
